import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, Users, Shield, Award, Target, 
  CheckCircle2, ArrowRight, Zap, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const values = [
  {
    icon: Shield,
    title: 'Fearless Functionality',
    description: 'We build technology that works. No excuses, no workarounds. Solutions that perform under pressure.'
  },
  {
    icon: Target,
    title: 'Focused Execution',
    description: 'Clear goals, measurable outcomes. We cut through complexity to deliver what matters most.'
  },
  {
    icon: Zap,
    title: 'Fresh Design',
    description: 'Modern, clean, and purposeful. Technology should feel intuitive, not intimidating.'
  },
  {
    icon: Heart,
    title: 'Genuine Partnership',
    description: 'Your success is our success. We invest in relationships, not just projects.'
  }
];

const whoWeServe = [
  'Small businesses ready to scale',
  'Entrepreneurs launching new ventures',
  'Organizations modernizing operations',
  'Event producers needing reliable tech',
  'Companies seeking digital transformation',
  'Non-profits expanding their reach'
];

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Fearless IT",
  "description": "Las Vegas-based technology company providing websites, automation, and event technology solutions.",
  "url": "https://fearlessit.io",
  "logo": "https://customer-assets.emergentagent.com/job_4e3d1843-41fa-499c-be06-175903ad91f6/artifacts/pmd7efeu_2.%20FearlessIt-horizontal_official.png",
  "foundingDate": "2015",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Las Vegas",
    "addressRegion": "NV",
    "addressCountry": "US"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "(702) 727-1773",
    "contactType": "customer service"
  },
  "sameAs": [
    "https://facebook.com/fearless.it",
    "https://instagram.com/fearless.it",
    "https://linkedin.com/company/fearless-it"
  ]
};

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      <SEO 
        title="About Fearless IT - Las Vegas Technology Company"
        description="Learn about Fearless IT, a Las Vegas-based technology company helping businesses grow through smart websites, AI automation, and event technology solutions. Trusted by businesses across Nevada."
        keywords="about Fearless IT, Las Vegas technology company, Las Vegas IT services, Nevada tech company, business technology partner, digital transformation Las Vegas"
        url="/company/about"
        structuredData={aboutSchema}
        isLocalPage={true}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full mb-6">
            <Building2 className="h-4 w-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700">About Us</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="about-title">
            About Fearless IT
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #0066FF, #5B3DF5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Technology That Helps Businesses Grow, Rank, and Run Flawlessly
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            Based in Las Vegas, we partner with businesses to build technology solutions that actually work. No jargon, no overcomplication. Just results.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-slate-gray leading-relaxed">
                <p>
                  Fearless IT was founded with a simple belief: technology should empower businesses, not frustrate them. Too many companies struggle with outdated systems, confusing software, and IT partners who speak in riddles.
                </p>
                <p>
                  We set out to change that. Starting in Chicago, IL, we built a reputation for delivering technology solutions that are powerful yet approachable. Solutions that business owners can actually understand and use.
                </p>
                <p>
                  Today, we serve clients across multiple industries, from local entrepreneurs to established organizations. We expanded to the Las Vegas valley where our headquarters now remain, with satellite operations in Chicago and a network of partners that extends virtually everywhere.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-8 border border-indigo-100">
              <h3 className="text-xl font-bold text-carbon-black mb-6">Quick Facts</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-carbon-black">Headquarters</div>
                    <div className="text-sm text-slate-gray">Las Vegas, Nevada</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 flex items-center justify-center flex-shrink-0">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-carbon-black">Clients Served</div>
                    <div className="text-sm text-slate-gray">30+ businesses across industries</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-carbon-black">Data Privacy Certified</div>
                    <div className="text-sm text-slate-gray">Agency Partner Status</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              What We Stand For
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Our approach to technology is guided by principles that put your success first.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const IconComponent = value.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center mb-4">
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-carbon-black mb-2">{value.title}</h3>
                  <p className="text-slate-gray text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Who We Serve
            </h2>
            <p className="text-lg text-slate-gray">
              We work with businesses of all sizes who share one thing in common: they're ready to grow.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {whoWeServe.map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                <span className="text-carbon-black font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Ready to Work With Us?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Let's discuss how Fearless IT can help your business grow with technology that works.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Get in Touch
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/company/mission">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-indigo-400 text-indigo-600 hover:bg-indigo-50 transition-all w-full sm:w-auto">
                Mission & Philosophy
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default AboutPage;
